<template>
    <div>
        <div class="mt-4 mb-8">
            <h2 class="text-3xl font-bold text-white">
                Sign in to your account
            </h2>
        </div>

        <form action="#" @submit.prevent="onSubmit">
            <div class="space-y-6">
                <UFormGroup label="Email address" :error="hasError">
                    <UInput 
                        v-model="form.email" 
                        type="email" 
                        size="xl" 
                        required
                    />
                </UFormGroup>
    
                <UFormGroup label="Password" :error="hasError && 'Incorrect email or password'">
                    <UInput 
                        ref="passwordInput" 
                        v-model="form.password" 
                        :type="passwordVisible ? 'text' : 'password'"
                        :ui="{ icon: { trailing: { pointer: 'pointer-events-auto' } } }"
                        size="xl" 
                        required
                    >
                        <template #trailing>
                            <button class="flex items-center justify-center cursor-pointer" type="button" @mousedown="passwordVisible = true" @mouseup="passwordVisible = false">
                                <UIcon v-if="passwordVisible" name="i-ph-eye-duotone" class="text-gray-500" />
                                <UIcon v-else name="i-ph-eye-slash-duotone" class="text-gray-500" />
                            </button>
                        </template>
                    </UInput>
                </UFormGroup>
            </div>

            <UButton block type="submit" size="xl" :loading="loading" color="black" class="mt-8">
                {{ loading ? 'Signing in...' : 'Continue' }}
            </UButton>
        </form>

        <div class="mt-6 text-center">
            <UButton to="/password/forgot" variant="link" color="black">
                Forgot your password?
            </UButton>
        </div>
    </div>
</template>

<script setup lang="ts">
const { $posthog } = useNuxtApp()
import { useStorage } from '@vueuse/core'

definePageMeta({ layout: 'auth', middleware: 'guest' })
useHead({ title: 'Login' })

const callCenter = useCallCenter()

const loading = ref<boolean>(false)
const hasError = ref<boolean>(false)
const passwordVisible = ref<boolean>(false)
const passwordInput = ref()

const form = ref({
    email: '',
    password: '',
})

async function onSubmit() {
    loading.value = true

    try {
        const session = await $fetch('/api/auth/login', {
            method: 'POST',
            body: form.value
        })

        if (session) {
            await useSupabaseClient().auth.setSession(session)
            callCenter.resetState()
            clearStorage()

            if (session.user.id) {
                $posthog().identify(session.user.id, { email: session.user.email })
            }

            navigateTo('/')
        } 
        else {
            showError()
        }
    } catch {
        showError()
    } finally {
        loading.value = false
    }
}

function clearStorage() {
    const customersStorage = useStorage('customers-list', {})
    const callsStorage = useStorage('calls-list', {})
    const invoicesStorage = useStorage('invoices-list', {})
    
    customersStorage.value = null
    callsStorage.value = null
    invoicesStorage.value = null
}

function showError() {
    passwordInput.value?.$refs.input.focus()
    hasError.value = true
}
</script>
